import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { io } from 'socket.io-client';
import { format, isSameDay, isToday, isYesterday } from 'date-fns';
import {
  FaHeadset, FaPaperPlane, FaPaperclip, FaSmile, FaBolt, FaExchangeAlt,
  FaTicketAlt, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobe,
  FaHistory, FaInfoCircle, FaEllipsisV, FaArrowLeft, FaCheck, FaHome, FaTimes,
  FaHourglassHalf, FaCheckCircle,
} from 'react-icons/fa';
import Swal from '../sweetalert2';
import { decodeJWT } from '../../../utils/jwt';
import * as api from '../../../services/atendimentoApi';
import { fixListingLinks, cardUrl } from '../../../utils/chatLinks';
import {
  Page, PageHeader, StatusSelect, Dot, Grid, Col, Tabs, Tab, SearchBox,
  ConvList, ConvItem, Avatar, ConvMeta, StatusPill, UnreadBadge, ThreadHeader,
  IconBtn, PrimaryBtn, Messages, DateSep, SysMsg, BubbleRow, Bubble, NewMsgsBar,
  Composer, SideSection, InfoRow, ImovelCard, SideActions, Empty, Reconnect, Skeleton,
  MdBody, MsgCards, MsgCard, PickerPanel, PickerItem, AttachChips, AttachChip,
} from './styles';

const CHAT_URL = import.meta.env.VITE_CHAT_URL;
const TABS = [
  { key: 'aguardando', label: 'Aguardando', icon: <FaHourglassHalf /> },
  { key: 'humano', label: 'Em atendimento', icon: <FaHeadset /> },
  { key: 'finalizado', label: 'Finalizados', icon: <FaCheckCircle /> },
];
const STATUS_META = {
  aguardando: { label: 'Aguardando', bg: '#dbeafe', c: '#1d4ed8' },
  humano: { label: 'Em atendimento', bg: '#dcfce7', c: '#166534' },
  ai: { label: 'Sónia', bg: '#fef3c7', c: '#92400e' },
  pendente: { label: 'Pendente', bg: '#fef9c3', c: '#a16207' },
  finalizado: { label: 'Finalizado', bg: '#f3f4f6', c: '#6b7280' },
  encerrada: { label: 'Finalizado', bg: '#f3f4f6', c: '#6b7280' },
};
const STATUS_DOT = { online: '#22c55e', ocupado: '#f59e0b', ausente: '#eab308', offline: '#9ca3af' };
const AVATAR_COLORS = ['#34574A', '#BA824F', '#6b7280', '#0e7490', '#7c3aed', '#be185d'];

const getToken = () => localStorage.getItem('auth_token') || '';
const me = () => decodeJWT(getToken()) || {};

const asArray = (data) => {
  const pick = (v) => (Array.isArray(v) ? v : null);
  if (!data) return [];
  return pick(data) || pick(data.sessions) || pick(data.items) || pick(data.mensagens) || pick(data.messages) || pick(data.results) || pick(data.data) || [];
};
// desembrulha payload de detalhe: aceita {mensagens,...} ou {data:{mensagens,...}}
const unwrapDetail = (data) => (data && (data.mensagens || data.messages) ? data : (data?.data && typeof data.data === 'object' ? data.data : data)) || {};
const sessionId = (s) => s?.id ?? s?.sessaoId ?? s?.codigoSESSAO ?? s?.codigo;
const statusMeta = (st) => STATUS_META[st] || STATUS_META.aguardando;
const initials = (nome) => String(nome || '?').trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
const avatarColor = (nome) => AVATAR_COLORS[Math.abs(String(nome || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length];
const msgTime = (m) => m?.data || m?.criadoEm || m?.dataEnvio || m?.createdAt || null;
const fmtHora = (d) => { try { return format(new Date(d), 'HH:mm'); } catch { return ''; } };
const fmtData = (d) => {
  try {
    const dt = new Date(d);
    if (isToday(dt)) return 'Hoje';
    if (isYesterday(dt)) return 'Ontem';
    return format(dt, 'dd/MM/yyyy');
  } catch { return ''; }
};
// mensagens humanas vêm como "assistant" prefixadas "[Nome] texto"
const stripAgentPrefix = (conteudo) => {
  const m = String(conteudo || '').match(/^\[([^\]]{2,60})\]\s*([\s\S]*)$/);
  return m ? { nome: m[1], texto: m[2] } : null;
};

const normalizeMsg = (m) => {
  const papel = m?.papel || m?.role || (m?.remetente ? 'user' : 'user');
  const conteudo = m?.conteudo ?? m?.content ?? m?.texto ?? '';
  return {
    id: m?.id ?? m?.codigo ?? m?.clientMsgId ?? `${msgTime(m)}:${String(conteudo).slice(0, 20)}`,
    papel,
    conteudo,
    agente: papel === 'assistant' ? stripAgentPrefix(conteudo) : null,
    anexo: m?.anexoUrl || m?.anexo || m?.attachmentUrl || null,
    cards: asArray(m?.imoveis || m?.cards),
    data: msgTime(m),
  };
};

const normalizeSessao = (s) => ({
  id: sessionId(s),
  protocolo: s?.protocolo || `#${sessionId(s) ?? ''}`,
  status: s?.status || 'aguardando',
  prioridade: s?.prioridade || 'normal',
  assunto: s?.assunto || null,
  visitante: s?.visitante || null,
  usuarioId: s?.usuarioId ?? null,
  atendente: s?.atendente || null,
  ticket: s?.ticket || null,
  ultimaMensagem: s?.ultimaMensagem || null,
  ultimaAtividade: s?.ultimaAtividade || s?.ultimaMensagem?.data || s?.criadoEm || null,
  naoLidas: s?.naoLidas ?? s?.unread ?? s?.mensagensNaoLidas ?? 0,
  raw: s,
});

const EVENTO_LABEL = (e) => {
  const t = e?.tipo || e?.evento || '';
  const por = e?.atendente?.nome || e?.por || '';
  const map = {
    abertura: 'Conversa iniciada.',
    assumir: por ? `Atendimento assumido por ${por}.` : 'Atendimento assumido.',
    transferir: por ? `Conversa transferida para ${por}.` : 'Conversa transferida.',
    finalizar: 'Atendimento finalizado.',
    reabrir: 'Conversa reaberta.',
    devolver: 'Conversa devolvida à Sónia.',
    ticket: 'Ticket criado.',
    identificacao: 'Visitante identificado.',
    handoff: 'Solicitado atendimento humano.',
  };
  return map[t] || e?.descricao || t;
};

export default function Atendimento() {
  const myId = me()?.codigo;
  const [tab, setTab] = useState('aguardando');
  const [q, setQ] = useState('');
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [counts, setCounts] = useState({ aguardando: 0, humano: 0, finalizado: 0 });
  const [selected, setSelected] = useState(null); // sessão normalizada
  const [detail, setDetail] = useState(null); // { messages, visitante, imovelInteresse, historicoRapido, eventos }
  const [detailLoading, setDetailLoading] = useState(false);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [myStatus, setMyStatus] = useState('online');
  const [atendentes, setAtendentes] = useState([]);
  const [connState, setConnState] = useState('connecting'); // connecting|online|reconnecting
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileView, setMobileView] = useState('list'); // list|chat|info
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(() => window.innerWidth > 768 && window.innerWidth <= 1100);
  const [infoOpen, setInfoOpen] = useState(false);
  const [newBelow, setNewBelow] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [respostas, setRespostas] = useState([]);
  const [attached, setAttached] = useState([]); // cards de imóvel a enviar
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState('');
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const selectedRef = useRef(null);
  const stickBottomRef = useRef(true);
  const messagesRef = useRef(null);
  const fileRef = useRef(null);
  const tabRef = useRef(tab);
  const qRef = useRef(q);
  const sessionIdsRef = useRef(new Set());
  const msgIdsRef = useRef(new Set());
  const imoveisCacheRef = useRef(null);

  useEffect(() => { selectedRef.current = selected; }, [selected]);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => { qRef.current = q; }, [q]);

  // ---------- loaders ----------
  const loadCounts = useCallback(async () => {
    try {
      const { res, data } = await api.getEstatisticas();
      if (res.ok && data) {
        const src = data.contadores || data.counts || data;
        setCounts((c) => ({
          aguardando: src.aguardando ?? src.aguardandoAtendimento ?? c.aguardando,
          humano: src.humano ?? src.emAtendimento ?? c.humano,
          finalizado: src.finalizado ?? c.finalizado,
        }));
      }
    } catch { /* noop */ }
  }, []);

  const loadSessions = useCallback(async ({ reset } = {}) => {
    const pg = reset ? 1 : pageNum;
    setLoading(true);
    setLoadErr(null);
    try {
      const { res, data } = await api.listSessions({ status: tabRef.current, page: pg, limit: 30, q: qRef.current || undefined });
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      const items = asArray(data).map(normalizeSessao);
      const tot = data?.total ?? data?.count ?? items.length;
      setSessions((prev) => (reset ? items : [...prev, ...items.filter(i => !sessionIdsRef.current.has(i.id))]));
      items.forEach(i => sessionIdsRef.current.add(i.id));
      setTotal(tot);
      setPageNum(pg + 1);
      setCounts((c) => ({ ...c, [tabRef.current]: typeof tot === 'number' ? tot : c[tabRef.current] }));
    } catch (e) {
      setLoadErr(e.message);
      if (reset) setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [pageNum]);

  const refreshCurrent = useCallback(() => {
    sessionIdsRef.current = new Set();
    setPageNum(1);
    loadSessions({ reset: true });
    loadCounts();
  }, [loadSessions, loadCounts]);

  // ref estável para o socket — evita recriar a conexão quando loadSessions muda de identidade
  const refreshRef = useRef(refreshCurrent);
  useEffect(() => { refreshRef.current = refreshCurrent; }, [refreshCurrent]);

  const loadDetail = useCallback(async (sess) => {
    setDetail(null);
    setDetailLoading(true);
    msgIdsRef.current = new Set();
    try {
      const { res, data } = await api.getSessionMessages(sess.id);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const payload = unwrapDetail(data);
      const msgs = asArray(payload.mensagens || payload.messages || payload).map(normalizeMsg);
      msgs.forEach(m => msgIdsRef.current.add(m.id));
      const eventos = asArray(payload.eventos).map((e, i) => ({
        id: `ev-${i}`, papel: 'system', conteudo: EVENTO_LABEL(e), data: e?.data || e?.criadoEm || null,
      }));
      // mescla eventos na linha do tempo
      const timeline = [...msgs, ...eventos.filter(e => e.conteudo)].sort((a, b) => new Date(a.data || 0) - new Date(b.data || 0));
      setDetail({
        messages: timeline,
        visitante: payload.visitante || sess.visitante || null,
        imovelInteresse: payload.imovelInteresse || null,
        historicoRapido: payload.historicoRapido || null,
      });
      stickBottomRef.current = true;
    } catch (e) {
      setDetail({ messages: [], visitante: sess.visitante, error: e.message });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openSession = useCallback((sess) => {
    setSelected(sess);
    setNewBelow(false);
    setMenuOpen(false);
    loadDetail(sess);
    if (isMobile) setMobileView('chat');
    // zera não-lidas localmente
    setSessions(prev => prev.map(s => s.id === sess.id ? { ...s, naoLidas: 0 } : s));
  }, [isMobile, loadDetail]);

  // ---------- socket ----------
  useEffect(() => {
    const socket = io(CHAT_URL, { auth: { token: getToken() } });
    const onConnect = () => setConnState('online');
    const onDisconnect = () => setConnState('reconnecting');
    const onErr = () => setConnState('reconnecting');
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onErr);

    const patchSessionInList = (sid, patch) => {
      setSessions(prev => prev.map(s => (s.id === sid ? { ...s, ...patch } : s)));
      setSelected(prev => (prev && prev.id === sid ? { ...prev, ...patch } : prev));
    };

    socket.on('sessao_mensagem', (p) => {
      const sid = p?.sessaoId ?? p?.id;
      if (sid == null) return;
      const isSel = selectedRef.current?.id === sid;
      patchSessionInList(sid, {
        ultimaMensagem: { papel: p.papel, conteudo: p.conteudo, data: new Date().toISOString() },
        ultimaAtividade: new Date().toISOString(),
        ...(isSel ? {} : { naoLidas: undefined }),
      });
      setSessions(prev => {
        const s = prev.find(x => x.id === sid);
        if (!s || isSel) return prev;
        return prev.map(x => x.id === sid ? { ...x, naoLidas: (x.naoLidas || 0) + 1 } : x);
      });
      if (isSel) {
        const msg = normalizeMsg({ papel: p.papel, conteudo: p.conteudo, data: new Date().toISOString(), id: p.clientMsgId || p.mensagemId });
        if (msgIdsRef.current.has(msg.id)) return;
        msgIdsRef.current.add(msg.id);
        setDetail(prev => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
        if (stickBottomRef.current) setTimeout(() => messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: 'smooth' }), 30);
        else setNewBelow(true);
      }
    });

    socket.on('sessao_identificada', (p) => {
      const sid = p?.sessaoId;
      if (sid == null) return;
      patchSessionInList(sid, { visitante: { nome: p.nome, email: p.email, whatsapp: p.whatsapp } });
      setDetail(prev => (prev && selectedRef.current?.id === sid)
        ? { ...prev, visitante: { ...(prev.visitante || {}), nome: p.nome, email: p.email, whatsapp: p.whatsapp } }
        : prev);
    });

    const refresh = () => refreshRef.current();
    socket.on('handoff_pedido', () => { refresh(); });
    socket.on('sessao_assumida', refresh);
    socket.on('sessao_transferida', refresh);
    socket.on('sessao_devolvida', refresh);
    socket.on('sessao_finalizada', refresh);

    return () => socket.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- efeitos de tela ----------
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 768);
      setIsTablet(w > 768 && w <= 1100);
      if (w > 1100) setInfoOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { refreshCurrent(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // busca com debounce
  useEffect(() => {
    const t = setTimeout(() => refreshCurrent(), 400);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // debounce da busca de imóveis no picker
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const t = setTimeout(() => searchImoveis(pickerQ), 300);
    return () => clearTimeout(t);
  }, [pickerQ, pickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const { res, data } = await api.listAtendentes();
        if (res.ok) setAtendentes(asArray(data));
      } catch { /* noop */ }
      try {
        const { res, data } = await api.listRespostas();
        if (res.ok) setRespostas(asArray(data));
      } catch { /* noop */ }
    })();
  }, []);

  // scroll do chat
  useEffect(() => {
    if (stickBottomRef.current && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [detail?.messages?.length]);

  // ---------- ações ----------
  const changeMyStatus = async (st) => {
    setMyStatus(st);
    try { await api.setMyStatus(st); } catch { /* noop */ }
  };

  // troca de aba ou apenas recarrega quando já está nela (o effect de `tab` já dispara refresh)
  const gotoTab = (key) => { if (tab === key) refreshCurrent(); else setTab(key); };

  const myNome = () => atendentes.find(a => (a.id ?? a.codigo) === myId)?.nome || me()?.nome || 'Eu';

  const assumir = async (sess) => {
    try {
      const { res, data } = await api.assumirSessao(sess.id);
      if (res.status === 409) {
        Swal.fire({ icon: 'info', title: 'Já assumida', text: data?.error || 'Esta conversa já foi assumida por outro atendente.', confirmButtonColor: '#34574A' });
        refreshCurrent();
        return;
      }
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const ret = data?.sessao || data?.session || data;
      setSelected(prev => (prev && prev.id === sess.id) ? {
        ...prev,
        status: ret?.status || 'humano',
        atendente: ret?.atendente || { id: myId, nome: myNome() },
      } : prev);
      refreshCurrent();
      loadDetail(sess);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.message, confirmButtonColor: '#34574A' });
    }
  };

  const send = async () => {
    const text = composer.trim();
    const cards = attached;
    if ((!text && !cards.length) || !selected || sending) return;
    setSending(true);
    try {
      const { res, data } = await api.enviarMensagem(selected.id, text, cards.length ? { imoveis: cards } : undefined);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setComposer('');
      setAttached([]);
      setPickerOpen(false);
      const ret = data?.mensagem || data?.message || data;
      const msg = normalizeMsg({
        papel: 'assistant',
        conteudo: ret?.conteudo ?? text,
        data: ret?.data || new Date().toISOString(),
        id: ret?.id,
        imoveis: ret?.imoveis || cards,
      });
      if (!msgIdsRef.current.has(msg.id)) {
        msgIdsRef.current.add(msg.id);
        setDetail(prev => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Falha ao enviar', text: e.message, confirmButtonColor: '#34574A' });
    } finally {
      setSending(false);
    }
  };

  // ---------- picker de imóveis ----------
  const mapImovel = (i) => ({
    id: i.codigoIMOVEL ?? i.id,
    titulo: i.tituloIMOVEL || i.titulo || i.nome || '',
    localizacao: [i.cityIMOVEL, i.stateIMOVEL].filter(Boolean).join(', ') || i.localizacao || i.enderecoIMOVEL || '',
    preco: i.preco || (i.valorIMOVEL ? `€ ${Number(i.valorIMOVEL).toLocaleString('pt-PT')}` : ''),
    quartos: i.quartosIMOVEL ?? i.quartos,
    casasDeBanho: i.casasBanhoIMOVEL ?? i.banheirosIMOVEL ?? i.casasDeBanho,
    capacidade: i.capacidadeIMOVEL ?? i.hospedesIMOVEL ?? i.capacidade,
    foto: i.fotos?.[0]?.urlFOTO || i.fotos?.[0]?.url || i.thumbnail || null,
    link: `/property/${i.codigoIMOVEL ?? i.id}${i.slugIMOVEL || i.slug ? `/${i.slugIMOVEL || i.slug}` : ''}`,
  });

  const loadImoveis = async () => {
    if (imoveisCacheRef.current) return imoveisCacheRef.current;
    const getArray = (d) => (Array.isArray(d) ? d : d?.items || d?.Registros || d?.data || d?.imoveis || []);
    const res = await fetch(`${import.meta.env.VITE_URL_API}/imoveis?page=1&pageSize=300&ativo=1`, {
      headers: { accept: 'application/json', Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const arr = getArray(await res.json()).filter(i =>
      !(Number(i?.excluidoIMOVEL) === 1 || i?.deleted_at || Number(i?.ativoIMOVEL) === -1)
    );
    imoveisCacheRef.current = arr;
    return arr;
  };

  const searchImoveis = async (term) => {
    setPickerLoading(true);
    try {
      const all = await loadImoveis();
      const t = String(term || '').toLowerCase().trim();
      const filtered = (t ? all.filter(i =>
        `${i.tituloIMOVEL || ''} ${i.cityIMOVEL || ''} ${i.enderecoIMOVEL || ''} ${i.codigoIMOVEL || ''}`.toLowerCase().includes(t)
      ) : all).slice(0, 8);
      setPickerItems(filtered.map(mapImovel));
    } catch {
      setPickerItems([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const togglePicker = () => {
    setPickerOpen(v => {
      const next = !v;
      if (next) searchImoveis(pickerQ);
      return next;
    });
  };

  const attachImovel = (card) => {
    if (!attached.some(c => c.id === card.id)) setAttached(prev => [...prev, card]);
    setPickerOpen(false);
  };

  const transferir = async () => {
    const outros = atendentes.filter(a => (a.id ?? a.codigo) !== myId);
    const { value: form } = await Swal.fire({
      title: 'Transferir atendimento',
      html: `
        <select id="sw-atd" class="swal2-select" style="width:80%;margin-bottom:8px">
          <option value="">Departamento/fila (sem atendente)</option>
          ${outros.map(a => `<option value="${a.id ?? a.codigo}">${a.nome || a.nomeUSUARIO || a.email}</option>`).join('')}
        </select>
        <input id="sw-motivo" class="swal2-input" placeholder="Motivo (opcional)" style="width:80%">`,
      showCancelButton: true,
      confirmButtonText: 'Transferir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#34574A',
      preConfirm: () => ({
        atendenteId: document.getElementById('sw-atd').value || undefined,
        motivo: document.getElementById('sw-motivo').value || undefined,
      }),
    });
    if (!form) return;
    try {
      const { res, data } = await api.transferirSessao(selected.id, form);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      Swal.fire({ icon: 'success', title: 'Transferida', timer: 1500, showConfirmButton: false });
      refreshCurrent();
      setSelected(null);
      if (isMobile) setMobileView('list');
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.message, confirmButtonColor: '#34574A' });
    }
  };

  const finalizar = async () => {
    const { value: motivo, isConfirmed } = await Swal.fire({
      title: 'Finalizar conversa?',
      input: 'text',
      inputPlaceholder: 'Motivo do encerramento (opcional)',
      showCancelButton: true,
      confirmButtonText: 'Finalizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      icon: 'warning',
    });
    if (!isConfirmed) return;
    try {
      const { res, data } = await api.finalizarSessao(selected.id, motivo);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      // finalizada: fecha a thread e mostra a conversa na aba Finalizados
      setSelected(null);
      setDetail(null);
      if (isMobile) setMobileView('list');
      gotoTab('finalizado');
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.message, confirmButtonColor: '#34574A' });
    }
  };

  const reabrir = async () => {
    try {
      const { res, data } = await api.reabrirSessao(selected.id);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      // reaberta já volta em atendimento com quem reabriu
      const ret = data?.sessao || data?.session || data;
      setSelected(prev => prev ? {
        ...prev,
        status: ret?.status || 'humano',
        atendente: ret?.atendente || { id: myId, nome: myNome() },
      } : prev);
      loadDetail(selected);
      gotoTab('humano');
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.message, confirmButtonColor: '#34574A' });
    }
  };

  const devolver = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Devolver à Sónia?',
      text: 'A IA volta a responder este visitante.',
      showCancelButton: true,
      confirmButtonText: 'Devolver',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#34574A',
    });
    if (!isConfirmed) return;
    try {
      const { res, data } = await api.devolverSessao(selected.id);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const ret = data?.sessao || data?.session || data;
      setSelected(prev => prev ? { ...prev, status: ret?.status || 'ai', atendente: ret?.atendente ?? null } : prev);
      refreshCurrent();
      loadDetail(selected);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.message, confirmButtonColor: '#34574A' });
    }
  };

  const criarTicket = async () => {
    const { value: resumo, isConfirmed } = await Swal.fire({
      title: 'Criar ticket',
      input: 'textarea',
      inputLabel: 'Resumo',
      inputValue: selected.assunto || `Atendimento ${selected.protocolo} — ${selected.visitante?.nome || 'visitante'}`,
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#34574A',
    });
    if (!isConfirmed) return;
    try {
      const { res, data } = await api.criarTicketSessao(selected.id, { resumo });
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const t = data?.ticket || data;
      Swal.fire({ icon: 'success', title: 'Ticket criado', text: t?.id ? `Ticket #${t.id}` : '', confirmButtonColor: '#34574A' });
      loadDetail(selected);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.message, confirmButtonColor: '#34574A' });
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selected) return;
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({ icon: 'warning', title: 'Arquivo muito grande', text: 'Máximo 10MB.', confirmButtonColor: '#34574A' });
      return;
    }
    try {
      const { res, data } = await api.enviarAnexoSessao(selected.id, file);
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const ret = data?.mensagem || data;
      const msg = normalizeMsg({ papel: 'assistant', conteudo: ret?.conteudo || '', anexoUrl: ret?.anexoUrl || ret?.anexo, data: new Date().toISOString(), id: ret?.id });
      msgIdsRef.current.add(msg.id);
      setDetail(prev => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Falha no anexo', text: err.message, confirmButtonColor: '#34574A' });
    }
  };

  const pickResposta = async () => {
    if (!respostas.length) {
      Swal.fire({ icon: 'info', title: 'Sem respostas rápidas', text: 'Nenhuma resposta cadastrada.', confirmButtonColor: '#34574A' });
      return;
    }
    const { value: idx } = await Swal.fire({
      title: 'Resposta rápida',
      input: 'select',
      inputOptions: Object.fromEntries(respostas.map((r, i) => [i, r.titulo || String(r.texto || r.conteudo || '').slice(0, 60)])),
      inputPlaceholder: 'Selecione...',
      showCancelButton: true,
      confirmButtonColor: '#34574A',
    });
    if (idx === undefined || idx === '') return;
    const r = respostas[Number(idx)];
    setComposer(prev => `${prev}${prev ? ' ' : ''}${r.texto || r.conteudo || r.titulo || ''}`);
  };

  const insertEmoji = (e) => {
    setComposer(prev => prev + e);
    setEmojiOpen(false);
  };

  // ---------- render helpers ----------
  const canWrite = selected && selected.status === 'humano' && (selected.atendente?.id == null || selected.atendente?.id === myId);
  const isFinished = selected && (selected.status === 'finalizado' || selected.status === 'encerrada');
  const canAssumir = selected && (selected.status === 'aguardando' || selected.status === 'ai');

  const renderBubble = (m, idx, arr) => {
    if (m.papel === 'tool') return null;
    if (m.papel === 'system' || m.papel === 'evento') {
      return <SysMsg key={m.id || idx}>{m.conteudo}</SysMsg>;
    }
    const showDate = idx === 0 || !isSameDay(new Date(arr[idx - 1]?.data || 0), new Date(m.data || 0));
    const mine = m.papel === 'assistant' || m.papel === 'agent' || m.papel === 'human';
    const texto = m.agente ? m.agente.texto : m.conteudo;
    return (
      <React.Fragment key={m.id || idx}>
        {showDate && m.data && <DateSep><span>{fmtData(m.data)}</span></DateSep>}
        <BubbleRow $me={mine}>
          <Bubble $me={mine}>
            {mine && m.agente?.nome && <span className="remetente">{m.agente.nome}</span>}
            {m.anexo && /\.(png|jpe?g|gif|webp)$/i.test(m.anexo)
              ? <a href={m.anexo} target="_blank" rel="noreferrer"><img className="anexo" src={m.anexo} alt="anexo" /></a>
              : m.anexo ? <a href={m.anexo} target="_blank" rel="noreferrer">📎 Arquivo anexado</a> : null}
            {texto && (
              <MdBody><ReactMarkdown>{fixListingLinks(texto)}</ReactMarkdown></MdBody>
            )}
            <span className="meta">{m.data ? fmtHora(m.data) : ''}</span>
          </Bubble>
        </BubbleRow>
        {m.cards?.length > 0 && (
          <MsgCards $me={mine}>
            {m.cards.map((c, ci) => (
              <MsgCard key={c.id || ci}>
                {c.foto
                  ? <img className="foto" src={c.foto} alt={c.titulo || 'Imóvel'} />
                  : <div className="nofoto">🏠</div>}
                <div className="body">
                  <div className="titulo">{c.titulo || 'Imóvel'}</div>
                  {c.localizacao && <div className="loc">📍 {c.localizacao}</div>}
                  {c.preco && <div className="preco">{c.preco}</div>}
                  <a href={cardUrl(c)} target="_blank" rel="noreferrer">Ver imóvel ↗</a>
                </div>
              </MsgCard>
            ))}
          </MsgCards>
        )}
      </React.Fragment>
    );
  };

  const visitor = detail?.visitante || selected?.visitante || {};
  const hist = detail?.historicoRapido || {};
  const imovel = detail?.imovelInteresse || null;

  const sidePanel = (
    <>
      <SideSection>
        <h4>Dados do visitante</h4>
        {visitor.nome && <InfoRow><FaUser /><span>{visitor.nome}</span></InfoRow>}
        {visitor.email && <InfoRow><FaEnvelope /><span>{visitor.email}</span></InfoRow>}
        {visitor.whatsapp && <InfoRow><FaPhone /><span>{visitor.whatsapp}</span></InfoRow>}
        {visitor.localizacao && <InfoRow><FaMapMarkerAlt /><span>{visitor.localizacao}</span></InfoRow>}
        {visitor.origem && <InfoRow><FaGlobe /><span>{visitor.origem}</span></InfoRow>}
        {!visitor.nome && !visitor.email && <InfoRow><span style={{ color: '#9ca3af' }}>Visitante anônimo — dados aparecem após identificação.</span></InfoRow>}
      </SideSection>

      {imovel && (
        <SideSection>
          <h4>Imóvel de interesse</h4>
          <ImovelCard>
            {imovel.foto && <img src={imovel.foto} alt={imovel.titulo || 'Imóvel'} />}
            <div className="body">
              <div className="titulo">{imovel.titulo || 'Imóvel'}</div>
              {imovel.localizacao && <div className="loc">📍 {imovel.localizacao}</div>}
              {imovel.preco && <div className="preco">{imovel.preco}</div>}
              <a href={`/property/${imovel.id}`} target="_blank" rel="noreferrer">Ver anúncio ↗</a>
            </div>
          </ImovelCard>
        </SideSection>
      )}

      <SideSection>
        <h4>Histórico rápido</h4>
        {hist.favoritos != null && <InfoRow><span>♥ {hist.favoritos} {hist.favoritos === 1 ? 'favorito' : 'favoritos'}</span></InfoRow>}
        {hist.reservasRealizadas != null && <InfoRow><span>📅 {hist.reservasRealizadas} {hist.reservasRealizadas === 1 ? 'reserva' : 'reservas'}</span></InfoRow>}
        {hist.visualizacoes != null && <InfoRow><span>👁 {hist.visualizacoes} visualizações</span></InfoRow>}
        {hist.conversasAnteriores != null && <InfoRow><span>💬 {hist.conversasAnteriores} conversas anteriores</span></InfoRow>}
        {hist.primeiroContato && <InfoRow><FaHistory /><span>Cliente desde {fmtData(hist.primeiroContato)}</span></InfoRow>}
        {Object.keys(hist).length === 0 && <InfoRow><span style={{ color: '#9ca3af' }}>Sem histórico disponível.</span></InfoRow>}
      </SideSection>

      {selected && !isFinished && (
        <SideActions>
          <button type="button" onClick={transferir}><FaExchangeAlt /> Transferir</button>
          <button type="button" onClick={criarTicket}><FaTicketAlt /> Criar ticket</button>
          {selected.status === 'humano' && <button type="button" onClick={devolver}><FaHeadset /> Devolver à Sónia</button>}
          <button type="button" className="danger" onClick={finalizar}><FaCheck /> Finalizar conversa</button>
        </SideActions>
      )}
      {isFinished && (
        <SideActions>
          <button type="button" onClick={reabrir}><FaHistory /> Reabrir conversa</button>
        </SideActions>
      )}
    </>
  );

  return (
    <Page>
      <PageHeader>
        <div>
          <h1>Atendimento</h1>
          <p>Chat online do portal</p>
        </div>
        <StatusSelect>
          <Dot $c={STATUS_DOT[myStatus]} />
          <select value={myStatus} onChange={(e) => changeMyStatus(e.target.value)} aria-label="Meu status">
            <option value="online">Online</option>
            <option value="ocupado">Ocupado</option>
            <option value="ausente">Ausente</option>
            <option value="offline">Offline</option>
          </select>
        </StatusSelect>
      </PageHeader>

      <Grid>
        {/* ============ COLUNA 1 — CONVERSAS ============ */}
        <Col $hideMobile={isMobile && mobileView !== 'list'}>
          <Tabs>
            {TABS.map(t => (
              <Tab key={t.key} $active={tab === t.key} title={t.label} onClick={() => setTab(t.key)}>
                {t.icon} <span className="lbl">{t.label}</span> <span className="n">{counts[t.key]}</span>
              </Tab>
            ))}
          </Tabs>
          <SearchBox>
            <input
              placeholder="Buscar conversa..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </SearchBox>
          <ConvList>
            {connState !== 'online' && <Reconnect>Reconectando ao chat…</Reconnect>}
            {loadErr && (
              <Empty>
                <span>Erro ao carregar conversas: {loadErr}</span>
                <PrimaryBtn type="button" onClick={refreshCurrent}>Tentar novamente</PrimaryBtn>
              </Empty>
            )}
            {!loadErr && sessions.map(s => (
              <ConvItem key={s.id} $active={selected?.id === s.id} onClick={() => openSession(s)}>
                <Avatar $c={avatarColor(s.visitante?.nome || s.protocolo)}>
                  {s.visitante?.foto ? <img src={s.visitante.foto} alt="" /> : initials(s.visitante?.nome || 'V')}
                </Avatar>
                <ConvMeta>
                  <div className="top">
                    <span className="nome">{s.visitante?.nome || `Visitante ${s.protocolo}`}</span>
                    <span className="hora">{s.ultimaAtividade ? fmtHora(s.ultimaAtividade) : ''}</span>
                  </div>
                  <div className="preview">{s.ultimaMensagem?.conteudo || s.assunto || '—'}</div>
                  <div className="bottom">
                    <StatusPill $bg={statusMeta(s.status).bg} $c={statusMeta(s.status).c}>{statusMeta(s.status).label}</StatusPill>
                    {s.prioridade === 'alta' && <StatusPill $bg="#fee2e2" $c="#b91c1c">Alta</StatusPill>}
                    {s.naoLidas > 0 && <UnreadBadge>{s.naoLidas}</UnreadBadge>}
                  </div>
                </ConvMeta>
              </ConvItem>
            ))}
            {!loadErr && loading && [1, 2, 3].map(i => <div key={i} style={{ padding: '0 14px' }}><Skeleton /><Skeleton /></div>)}
            {!loadErr && !loading && sessions.length === 0 && (
              <Empty>
                <FaHeadset />
                <span>{tab === 'aguardando' ? 'Não há conversas aguardando atendimento.' : 'Nenhuma conversa nesta aba.'}</span>
              </Empty>
            )}
            {!loading && sessions.length < total && (
              <div style={{ padding: 10, textAlign: 'center' }}>
                <PrimaryBtn type="button" onClick={() => loadSessions()}>Carregar mais</PrimaryBtn>
              </div>
            )}
          </ConvList>
        </Col>

        {/* ============ COLUNA 2 — CHAT ============ */}
        <Col $hideMobile={isMobile && mobileView !== 'chat'}>
          {!selected ? (
            <Empty>
              <FaHeadset />
              <span>Selecione uma conversa para iniciar o atendimento.</span>
            </Empty>
          ) : (
            <>
              <ThreadHeader>
                {isMobile && <IconBtn onClick={() => setMobileView('list')}><FaArrowLeft /></IconBtn>}
                <Avatar $c={avatarColor(visitor.nome || selected.protocolo)}>
                  {visitor.foto ? <img src={visitor.foto} alt="" /> : initials(visitor.nome || 'V')}
                </Avatar>
                <div className="info">
                  <div className="nome">{visitor.nome || `Visitante ${selected.protocolo}`}</div>
                  <div className="sub">
                    {[visitor.email, visitor.whatsapp, selected.protocolo].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <StatusPill $bg={statusMeta(selected.status).bg} $c={statusMeta(selected.status).c}>
                  {statusMeta(selected.status).label}
                </StatusPill>
                {(isTablet || isMobile) && (
                  <IconBtn title="Dados do visitante" onClick={() => (isMobile ? setMobileView('info') : setInfoOpen(v => !v))}><FaInfoCircle /></IconBtn>
                )}
                <div style={{ position: 'relative' }}>
                  <IconBtn title="Ações" onClick={() => setMenuOpen(v => !v)}><FaEllipsisV /></IconBtn>
                  {menuOpen && (
                    <div style={{ position: 'absolute', right: 0, top: 36, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', zIndex: 50, minWidth: 190 }}>
                      {[
                        canAssumir && { l: 'Assumir atendimento', f: () => assumir(selected) },
                        selected.status === 'humano' && { l: 'Transferir', f: transferir },
                        selected.status === 'humano' && { l: 'Devolver à Sónia', f: devolver },
                        { l: 'Criar ticket', f: criarTicket },
                        isFinished && { l: 'Reabrir', f: reabrir },
                        !isFinished && { l: 'Finalizar conversa', f: finalizar, danger: true },
                      ].filter(Boolean).map((a, i) => (
                        <button key={i} type="button" onClick={() => { setMenuOpen(false); a.f(); }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', background: 'none', fontSize: 13, color: a.danger ? '#dc2626' : '#1f2937', cursor: 'pointer' }}>
                          {a.l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </ThreadHeader>

              <Messages ref={messagesRef} onScroll={(e) => {
                const el = e.target;
                stickBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
                if (stickBottomRef.current) setNewBelow(false);
              }}>
                {detailLoading && [1, 2, 3, 4].map(i => <Skeleton key={i} style={{ width: `${70 - i * 10}%` }} />)}
                {!detailLoading && detail?.error && (
                  <Empty>
                    <span>{detail.error}</span>
                    <PrimaryBtn type="button" onClick={() => loadDetail(selected)}>Tentar novamente</PrimaryBtn>
                  </Empty>
                )}
                {!detailLoading && detail && detail.messages.map(renderBubble)}
                {!detailLoading && detail && detail.messages.length === 0 && (
                  <Empty><span>Sem mensagens ainda.</span></Empty>
                )}
                {newBelow && <NewMsgsBar type="button" onClick={() => { messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: 'smooth' }); setNewBelow(false); }}>Novas mensagens ↓</NewMsgsBar>}
              </Messages>

              {canAssumir && (
                <Composer>
                  <PrimaryBtn type="button" style={{ margin: '0 auto' }} onClick={() => assumir(selected)}>
                    <FaHeadset /> Assumir atendimento
                  </PrimaryBtn>
                </Composer>
              )}
              {isFinished && (
                <Composer><div style={{ margin: '0 auto', fontSize: 13, color: '#6b7280' }}>Conversa finalizada — somente leitura.</div></Composer>
              )}
              {canWrite && (
                <>
                  {attached.length > 0 && (
                    <AttachChips>
                      {attached.map(c => (
                        <AttachChip key={c.id}>
                          🏠 <span className="t">{c.titulo || `Imóvel ${c.id}`}</span>
                          <button type="button" title="Remover" onClick={() => setAttached(prev => prev.filter(p => p.id !== c.id))}><FaTimes /></button>
                        </AttachChip>
                      ))}
                    </AttachChips>
                  )}
                <Composer>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={onFile} />
                  <IconBtn title="Anexar" onClick={() => fileRef.current?.click()}><FaPaperclip /></IconBtn>
                  <IconBtn title="Anexar imóvel" onClick={togglePicker}><FaHome /></IconBtn>
                  {pickerOpen && (
                    <PickerPanel>
                      <div className="picker-search">
                        <input
                          autoFocus
                          placeholder="Buscar imóvel por título, cidade ou código..."
                          value={pickerQ}
                          onChange={(e) => setPickerQ(e.target.value)}
                        />
                      </div>
                      <div className="picker-list">
                        {pickerLoading && <div className="picker-empty">Buscando...</div>}
                        {!pickerLoading && pickerItems.map(c => (
                          <PickerItem key={c.id} type="button" onClick={() => attachImovel(c)}>
                            {c.foto
                              ? <img className="thumb" src={c.foto} alt="" />
                              : <span className="thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏠</span>}
                            <div className="info">
                              <div className="t">{c.titulo || `Imóvel ${c.id}`}</div>
                              <div className="s">{[c.localizacao, c.preco].filter(Boolean).join(' · ')}</div>
                            </div>
                          </PickerItem>
                        ))}
                        {!pickerLoading && pickerItems.length === 0 && (
                          <div className="picker-empty">Nenhum imóvel encontrado.</div>
                        )}
                      </div>
                    </PickerPanel>
                  )}
                  <div style={{ position: 'relative' }}>
                    <IconBtn title="Emoji" onClick={() => setEmojiOpen(v => !v)}><FaSmile /></IconBtn>
                    {emojiOpen && (
                      <div style={{ position: 'absolute', bottom: 38, left: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 6, display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 2, zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
                        {['😀', '😄', '👍', '🙏', '🏠', '🔑', '📅', '💰', '✅', '❌', '😊', '🤝'].map(e => (
                          <button key={e} type="button" onClick={() => insertEmoji(e)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', padding: 4 }}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <IconBtn title="Respostas rápidas" onClick={pickResposta}><FaBolt /></IconBtn>
                  <textarea
                    rows={1}
                    placeholder="Digite sua mensagem..."
                    value={composer}
                    onChange={(e) => { setComposer(e.target.value); e.target.style.height = 'auto'; e.target.style.height = `${Math.min(e.target.scrollHeight, 110)}px`; }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  />
                  <IconBtn title="Enviar" onClick={send} disabled={sending || (!composer.trim() && !attached.length)}>
                    <FaPaperPlane />
                  </IconBtn>
                </Composer>
                </>
              )}
              {selected.status === 'humano' && !canWrite && !isFinished && !canAssumir && (
                <Composer><div style={{ margin: '0 auto', fontSize: 13, color: '#6b7280' }}>Em atendimento por {selected.atendente?.nome || 'outro atendente'}.</div></Composer>
              )}
            </>
          )}
        </Col>

        {/* ============ COLUNA 3 — VISITANTE ============ */}
        <Col
          $hideMobile={isMobile && mobileView !== 'info'}
          $hideTablet={isTablet && !infoOpen}
          style={isTablet && infoOpen ? { position: 'fixed', right: 16, top: 90, bottom: 20, width: 320, zIndex: 200, boxShadow: '0 12px 40px rgba(0,0,0,.2)' } : undefined}
        >
          {isMobile && (
            <div style={{ padding: 10, borderBottom: '1px solid #e5e7eb' }}>
              <IconBtn onClick={() => setMobileView('chat')}><FaArrowLeft /></IconBtn>
            </div>
          )}
          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {!selected ? (
              <Empty><FaUser /><span>Selecione uma conversa para ver os dados.</span></Empty>
            ) : sidePanel}
          </div>
        </Col>
      </Grid>
    </Page>
  );
}
